import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ApiClientService } from './api-client.service';
import { StorageService } from './storage.service';
import { ApplicationConfigurationLoaderService } from '../authorization/application-configuration-loader.service';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  UserInfo
} from '../models';

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const USER_KEY = 'auth_user';

/**
 * Service for managing authentication state.
 * CPNotes: Slim JWT + refresh rotation; permissions loaded via application-configuration
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiClient = inject(ApiClientService);
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);
  private readonly configurationLoader = inject(ApplicationConfigurationLoaderService);

  private readonly _currentUser = signal<LoginResponse | null>(null);
  private readonly _isAuthenticated = signal<boolean>(false);

  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this._isAuthenticated());
  readonly grantedPolicies = this.configurationLoader.grantedPolicies;
  readonly authorizationReady = this.configurationLoader.ready;

  constructor() {
    this.restoreUserFromStorage();
  }

  /**
   * Gets the current user's permission names from granted policies.
   */
  get permissions(): string[] {
    const policies = this.configurationLoader.grantedPolicies();
    return Object.keys(policies).filter(key => policies[key]);
  }

  /**
   * Gets the current user info.
   */
  get userInfo(): UserInfo | null {
    return this._currentUser()?.user || null;
  }

  /**
   * Gets the JWT access token.
   */
  get token(): string | null {
    return this._currentUser()?.accessToken || null;
  }

  /**
   * Gets the refresh token.
   */
  get refreshTokenValue(): string | null {
    return this._currentUser()?.refreshToken
      || this.storage.getItem<string>(REFRESH_TOKEN_KEY);
  }

  /**
   * Authenticates a user, stores tokens, then loads granted policies.
   * Config load failure clears the session (hard fail).
   */
  login(request: LoginRequest): Observable<LoginResponse | null> {
    return this.apiClient.post<LoginResponse>('auth/token', request).pipe(
      switchMap(response => {
        if (!response?.accessToken) {
          return of(null);
        }
        this.setSession(response);
        return this.configurationLoader.load().pipe(
          map(() => response),
          catchError(() => {
            this.clearUser();
            this.configurationLoader.clear();
            return of(null);
          })
        );
      }),
      catchError(error => {
        console.error('Login error:', error);
        this.clearUser();
        this.configurationLoader.clear();
        return of(null);
      })
    );
  }

  /**
   * Rotates the refresh token and returns the new access token.
   */
  refreshAccessToken(): Observable<string> {
    const refreshToken = this.refreshTokenValue;
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    const body: RefreshTokenRequest = { refreshToken };
    return this.apiClient.post<LoginResponse>('auth/refresh', body).pipe(
      tap(response => {
        if (response?.accessToken) {
          this.setSession(response);
        }
      }),
      map(response => {
        if (!response?.accessToken) {
          throw new Error('Refresh failed');
        }
        return response.accessToken;
      })
    );
  }

  /**
   * Reloads granted policies for the current user (e.g. after role grant changes).
   */
  refreshCurrentAuthorization(): Observable<void> {
    return this.configurationLoader.load();
  }

  /**
   * Logs out the current user and revokes the refresh token when possible.
   */
  logout(): void {
    const refreshToken = this.refreshTokenValue;
    if (refreshToken) {
      const body: RefreshTokenRequest = { refreshToken };
      this.apiClient.post<void>('auth/logout', body).subscribe({
        error: () => { /* ignore revoke failures on logout */ }
      });
    }

    this.clearUser();
    this.configurationLoader.clear();
    this.router.navigate(['/login']);
  }

  /**
   * Sets the current session after login or refresh.
   */
  setSession(session: LoginResponse): void {
    this._currentUser.set(session);
    this._isAuthenticated.set(true);
    this.storage.setItem(TOKEN_KEY, session.accessToken);
    this.storage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
    this.storage.setItem(USER_KEY, session);
  }

  /**
   * Clears the current user (logout / hard-fail).
   */
  clearUser(): void {
    this._currentUser.set(null);
    this._isAuthenticated.set(false);
    this.storage.removeItem(TOKEN_KEY);
    this.storage.removeItem(REFRESH_TOKEN_KEY);
    this.storage.removeItem(USER_KEY);
  }

  /**
   * Checks if the current user has a specific permission.
   */
  hasPermission(permission: string): boolean {
    return this.configurationLoader.hasPermission(permission);
  }

  /**
   * Checks if the current user has any of the specified permissions.
   */
  hasAnyPermission(...permissions: string[]): boolean {
    return this.configurationLoader.hasAnyPermission(...permissions);
  }

  /**
   * Checks if the current user has all of the specified permissions.
   */
  hasAllPermissions(...permissions: string[]): boolean {
    return this.configurationLoader.hasAllPermissions(...permissions);
  }

  /**
   * Restores user from storage on app initialization and refreshes policies.
   */
  private restoreUserFromStorage(): void {
    const token = this.storage.getItem<string>(TOKEN_KEY);
    const refreshToken = this.storage.getItem<string>(REFRESH_TOKEN_KEY);
    const user = this.storage.getItem<LoginResponse>(USER_KEY);

    if (token && refreshToken && user && user.accessToken === token) {
      this._currentUser.set({
        ...user,
        accessToken: token,
        refreshToken
      });
      this._isAuthenticated.set(true);
      this.configurationLoader.hydrateFromStorage();

      this.configurationLoader.load().subscribe({
        error: () => {
          this.clearUser();
          this.configurationLoader.clear();
          void this.router.navigate(['/login']);
        }
      });
    } else {
      this.clearUser();
      this.configurationLoader.clear();
    }
  }
}
